import type { Cell, Sheet } from "@fortune-sheet/core";
import { jsPDF } from "jspdf";
import autoTable, { type CellDef, type CellHook, type Color, type OverflowType, type Styles } from "jspdf-autotable";
import { DateTime } from "luxon";
import { type HeaderFooterFields, type HeaderFooterRun, type HeaderFooterSections, parseExcelHeaderFooter } from "../utils/excelHeaderFooter";
import {
	PAPER_SIZE_LETTER,
	type PageMarginsPt,
	type PaperDimensionsPt,
	type PrintTitleRows,
	paperDimensionsForCode,
	type SheetPageSetup,
} from "../utils/excelPageSetup";
import { EXCEL_DEFAULT_COLUMN_WIDTH_PX, EXCEL_DEFAULT_FONT_SIZE_PT, EXCEL_DEFAULT_ROW_HEIGHT_PT } from "../utils/excelToFortuneSheet";
import { PrintoutOrientation } from "../utils/models";

/** Fortune-Sheet column widths and row heights are in pixels (~96dpi); jsPDF unit is points (72dpi). */
const PX_TO_PT = 0.75;
const POINTS_PER_INCH = 72;

/** Excel's default body size, for a cell with no font of its own. Helvetica stays the drawn face — jsPDF
 *  has no Calibri — so wrap points shift a character; the page geometry follows the size, not the face. */
const BODY_FONT_SIZE_PT = EXCEL_DEFAULT_FONT_SIZE_PT;

/** Excel's cell inset is ~2.25pt across the cell; autoTable's `cellPadding` is per side. */
const CELL_PADDING_PT = 1.125;

const GRID_LINE_WIDTH_PT = 0.5;
const GRID_LINE_COLOR: Color = [211, 211, 211];

/** autoTable's `grid` theme styles a head row teal and bold. Our head is the sheet's OWN repeated rows,
 *  not decoration, so it is put back to the theme's plain body values; each cell's `styles` still win. */
const PLAIN_BODY_FILL_COLOR: Color = 255;
const PLAIN_BODY_TEXT_COLOR: Color = 80;

/** Excel's default header/footer font is the body default, and its zoom never touches page chrome. */
const HEADER_FOOTER_FONT_SIZE_PT = 11;
const HEADER_FOOTER_FONT_FACE = "helvetica";

/** Excel's fit-to scale range: it never enlarges, so 100 is the ceiling, and below 10 it stops shrinking
 *  and lets the rest of the sheet spill onto extra pages. */
const MIN_SCALE_PERCENT = 10;
const MAX_SCALE_PERCENT = 100;
const PERCENT = 100;

/** Float slack for `packIntoPageRuns`, so an exactly-one-page fit isn't rounded onto a spurious second. */
const PAGE_FIT_EPSILON_PT = 0.01;

/** DEFAULT: paper for a sheet that declares no `paperSize`, or one `paperDimensionsForCode` cannot map.
 *  jsPDF needs a format at construction, and Letter is what this renderer has always used. */
const FALLBACK_PAPER_SIZE_CODE = PAPER_SIZE_LETTER;

/** DEFAULT: Excel's own "Normal" preset, for a hand-built Fortune-Sheet carrying no page setup. A sheet
 *  converted from a workbook always has real margins — ExcelJS materialises Excel's defaults on parse. */
const EXCEL_NORMAL_MARGINS_PT: PageMarginsPt = {
	left: 0.7 * POINTS_PER_INCH,
	right: 0.7 * POINTS_PER_INCH,
	top: 0.75 * POINTS_PER_INCH,
	bottom: 0.75 * POINTS_PER_INCH,
	header: 0.3 * POINTS_PER_INCH,
	footer: 0.3 * POINTS_PER_INCH,
};

/** A sheet as `excelBufferToFortuneSheet` produces it. `pageSetup` is absent on a hand-built sheet. */
export type PrintoutSheet = Sheet & { pageSetup?: SheetPageSetup };

const hexToRgb = (hex?: string | null): Color | undefined => {
	if (!hex) return undefined;
	const v = hex.replace("#", "");
	if (v.length !== 6) return undefined;
	const r = Number.parseInt(v.slice(0, 2), 16);
	const g = Number.parseInt(v.slice(2, 4), 16);
	const b = Number.parseInt(v.slice(4, 6), 16);
	if ([r, g, b].some(Number.isNaN)) return undefined;
	return [r, g, b];
};

/** Fortune-Sheet's `vt` as autoTable's `valign`. Throws rather than guessing: a silently wrong vertical
 *  alignment is invisible in review. */
const verticalAlignFor = (vt: number | undefined): Styles["valign"] | undefined => {
	if (vt === undefined) return undefined;
	switch (vt) {
		case 0:
			return "middle";
		case 1:
			return "top";
		case 2:
			return "bottom";
		default:
			throw new Error(`Unknown Fortune-Sheet vertical alignment code: ${vt}`);
	}
};

/**
 * Underline and strikethrough, which autoTable's `Styles` has no concept of at all — it sets a font
 * face, and helvetica has no underlined face. Carried on the cell definition itself because
 * `new Cell(rawCell, …)` stores what it is given verbatim, so `data.cell.raw` inside a cell hook IS this
 * object; the rules are then drawn in `didDrawCell`. That one documented public field is the only
 * autoTable internal this relies on.
 */
type TextDecoration = { underline: boolean; strikeThrough: boolean };

type DecoratedCellDef = CellDef & { textDecoration?: TextDecoration };

/** One visible (non-merge-covered) cell, tagged with the sheet column it starts at so a column-banded
 *  page (see `packIntoPageRuns`) can filter a row down to just the entries that belong on it. */
type PositionedCell = { cell: DecoratedCellDef; startCol: number };

/** A cell that carries its own font size, paired with the size the WORKBOOK gave it. `styles` is the live
 *  object autoTable renders, shared by every band, so scaling it from itself compounds; the scaled size is
 *  recomputed from `originalFontSizePt` instead, which makes applying it idempotent. */
type ExplicitFontStyle = { styles: Partial<Styles>; originalFontSizePt: number };

type SheetBody = {
	rows: PositionedCell[][];
	maxCol: number;
	explicitFonts: ExplicitFontStyle[];
	/** The largest font any cell of each row asks for — the row's height floor, see `effectiveRowHeightsPt`. */
	rowFontSizesPt: number[];
};

/** Fortune Sheet's `1` for an attribute the cell turns on; `0` is the same attribute explicitly off. */
const ATTR_ON = 1;

/**
 * Bold and italic resolved together, because a font FACE is one value: helvetica ships all four and
 * autoTable's `FontStyle` union names all four, so an italic cell that is also bold must ask for
 * `bolditalic` rather than losing one of the two.
 *
 * `undefined` for a plain cell rather than `"normal"`, so a cell that declares nothing keeps deferring to
 * the table's own `styles`/`headStyles` exactly as it did before.
 */
const fontStyleForCell = (cell: Cell | null | undefined): Styles["fontStyle"] | undefined => {
	const bold = cell?.bl === ATTR_ON;
	const italic = cell?.it === ATTR_ON;
	if (bold && italic) return "bolditalic";
	if (bold) return "bold";
	if (italic) return "italic";
	return undefined;
};

/** Everything the workbook says about a cell except its size, which the fit-to scale has to touch. */
const unscaledStylesForCell = (cell: Cell | null | undefined): Partial<Styles> => {
	const styles: Partial<Styles> = {};
	const fontStyle = fontStyleForCell(cell);
	if (fontStyle) styles.fontStyle = fontStyle;
	const textColor = hexToRgb(cell?.fc);
	if (textColor) styles.textColor = textColor;
	const fillColor = hexToRgb(cell?.bg);
	if (fillColor) styles.fillColor = fillColor;
	if (cell?.ht === 0) styles.halign = "center";
	else if (cell?.ht === 2) styles.halign = "right";
	const valign = verticalAlignFor(cell?.vt);
	if (valign) styles.valign = valign;
	return styles;
};

const buildSheetBody = (sheet: PrintoutSheet): SheetBody => {
	let maxRow = 0;
	let maxCol = 0;
	const cellMap: Record<string, Cell | null> = {};
	for (const cd of sheet.celldata ?? []) {
		cellMap[`${cd.r}_${cd.c}`] = cd.v;
		if (cd.v != null) {
			maxRow = Math.max(maxRow, cd.r);
			maxCol = Math.max(maxCol, cd.c);
		}
	}

	const mergedCovered = new Set<string>();
	const mergeOrigins: Record<string, { rs: number; cs: number }> = {};
	for (const m of Object.values(sheet.config?.merge ?? {})) {
		mergeOrigins[`${m.r}_${m.c}`] = { rs: m.rs, cs: m.cs };
		for (let dr = 0; dr < m.rs; dr++) {
			for (let dc = 0; dc < m.cs; dc++) {
				if (dr === 0 && dc === 0) continue;
				mergedCovered.add(`${m.r + dr}_${m.c + dc}`);
				if (m.r + dr > maxRow) maxRow = m.r + dr;
				if (m.c + dc > maxCol) maxCol = m.c + dc;
			}
		}
	}

	const explicitFonts: ExplicitFontStyle[] = [];
	const rows: PositionedCell[][] = [];
	const rowFontSizesPt: number[] = [];
	for (let r = 0; r <= maxRow; r++) {
		const row: PositionedCell[] = [];
		let largestFontPt = BODY_FONT_SIZE_PT;
		for (let c = 0; c <= maxCol; c++) {
			if (mergedCovered.has(`${r}_${c}`)) continue;
			const cell = cellMap[`${r}_${c}`];
			const merge = mergeOrigins[`${r}_${c}`];
			const styles = unscaledStylesForCell(cell);
			// Excel's scale is a true zoom, so a cell with its own size shrinks along with everything else.
			if (typeof cell?.fs === "number") {
				explicitFonts.push({ styles, originalFontSizePt: cell.fs });
				largestFontPt = Math.max(largestFontPt, cell.fs);
			}
			const underline = cell?.un === ATTR_ON;
			const strikeThrough = cell?.cl === ATTR_ON;
			row.push({
				startCol: c,
				cell: {
					content: String(cell?.m ?? cell?.v ?? ""),
					rowSpan: merge?.rs,
					colSpan: merge?.cs,
					styles,
					// Absent unless the cell asks for one, so the hook's cheap early-out covers every cell of
					// every printout that uses neither.
					...(underline || strikeThrough ? { textDecoration: { underline, strikeThrough } } : undefined),
				},
			});
		}
		rows.push(row);
		rowFontSizesPt.push(largestFontPt);
	}

	return { rows, maxCol, explicitFonts, rowFontSizesPt };
};

/**
 * Greedy packing of scaled sizes into page-sized runs, in either axis — how Excel paginates: never split
 * an item, never reorder, leave one bigger than the page on its own page to spill. Deliberately unaware
 * of merges: a title merged across the *entire* width would veto every boundary it touches and can leave
 * the sheet nowhere to split at all. A straddling merge is clipped in `drawBand` instead.
 */
const packIntoPageRuns = (sizesPt: number[], scale: number, capacityPt: number): Array<{ start: number; end: number }> => {
	const runs: Array<{ start: number; end: number }> = [];
	let start = 0;
	let usedPt = 0;
	for (let i = 0; i < sizesPt.length; i++) {
		const sizePt = sizesPt[i] * scale;
		if (i > start && usedPt + sizePt > capacityPt + PAGE_FIT_EPSILON_PT) {
			runs.push({ start, end: i - 1 });
			start = i;
			usedPt = 0;
		}
		usedPt += sizePt;
	}
	runs.push({ start, end: sizesPt.length - 1 });
	return runs;
};

const sumPt = (sizesPt: number[]): number => sizesPt.reduce((total, size) => total + size, 0);

type ScaleSearchArgs = {
	columnWidthsPt: number[];
	/** Only the rows that FLOW down the page — the repeated print titles are budgeted separately. */
	flowRowHeightsPt: number[];
	/** Unscaled height of the print-title block reserved at the top of every page; 0 when there is none. */
	printTitleHeightPt: number;
	availableWidthPt: number;
	availableHeightPt: number;
	/** `null` when Excel's fit-to-width is off for this print job — the axis is then unconstrained. */
	widthPages: number | null;
	/** `null` when Excel's fit-to-height is off. */
	heightPages: number | null;
};

/**
 * Excel's single fit-to scale percentage: the largest whole percent, never above 100, at which the real
 * page packing respects BOTH active page budgets. One zoom covers fonts, widths, heights, padding and
 * borders; margins are physical and stay out of it.
 *
 * The print-title block comes out of every page's height budget rather than flowing — Excel reprints it
 * on each page — and scales too, hence recomputed inside the scan. The scan is a descending linear one,
 * not a bisection: greedy packing is not monotone in the scale (a column that stops fitting rolls its
 * whole width onto the next page), so a bisection can land on the wrong side of a step.
 */
const resolveScalePercent = ({
	columnWidthsPt,
	flowRowHeightsPt,
	printTitleHeightPt,
	availableWidthPt,
	availableHeightPt,
	widthPages,
	heightPages,
}: ScaleSearchArgs): number => {
	if (widthPages === null && heightPages === null) return MAX_SCALE_PERCENT;

	for (let percent = MAX_SCALE_PERCENT; percent >= MIN_SCALE_PERCENT; percent--) {
		const scale = percent / PERCENT;
		const widthFits = widthPages === null || packIntoPageRuns(columnWidthsPt, scale, availableWidthPt).length <= widthPages;
		const flowCapacityPt = availableHeightPt - printTitleHeightPt * scale;
		// Titles alone taller than the page leave no room to flow into; only a smaller scale can help.
		const heightFits = heightPages === null || (flowCapacityPt > 0 && packIntoPageRuns(flowRowHeightsPt, scale, flowCapacityPt).length <= heightPages);
		if (widthFits && heightFits) return percent;
	}
	// Excel clamps at its floor and spills what still doesn't fit onto extra pages.
	return MIN_SCALE_PERCENT;
};

/** The paper the whole document prints on — resolved once, not per sheet, because jsPDF fixes a page's
 *  size when it is added and autoTable adds its own continuation pages with no format argument. */
const resolvePaperPt = (sheets: PrintoutSheet[]): PaperDimensionsPt => {
	const declared = sheets.find((sheet) => sheet.pageSetup?.paperDimensionsPt)?.pageSetup?.paperDimensionsPt;
	if (declared) return declared;

	const fallback = paperDimensionsForCode(FALLBACK_PAPER_SIZE_CODE);
	// Unreachable unless PAPER_SIZE_LETTER stops being mapped — loud beats a silently wrong page size.
	if (!fallback) throw new Error("Letter paper dimensions are missing from the paper size table");
	return fallback;
};

const resolveMarginsPt = (pageSetup: SheetPageSetup | undefined): PageMarginsPt => pageSetup?.marginsPt ?? EXCEL_NORMAL_MARGINS_PT;

/** Which way up the paper goes, for the whole document — resolved once for the same reason the paper size
 *  is. The caller wins only where it has a real user choice (Daily, Monthly, 530 and Week pass what their
 *  settings panel shows); Facility and Disciplinary pass `null` and let the sheet speak for itself. */
const resolveOrientation = (sheets: PrintoutSheet[], requested: PrintoutOrientation | null): PrintoutOrientation => {
	if (requested !== null) return requested;
	const declared = sheets.find((sheet) => sheet.pageSetup?.orientation)?.pageSetup?.orientation;
	// DEFAULT: portrait — Excel's own, so this only names the value already in force for the hand-built
	// sheets that carry no page setup at all.
	return declared === "landscape" ? PrintoutOrientation.Landscape : PrintoutOrientation.Portrait;
};

/** What the caller asked for on each fit-to axis; `null` where the caller has no user setting at all. */
type FitRequest = { fitToPage: boolean | null; fitToWidth: number | null; fitToHeight: number | null };

/** DEFAULT: the budget for a caller with no printout settings whose sheet does not ask to fit either. One
 *  page wide, height unbounded, is what they have always produced; tightening it would reflow printouts. */
const UNSET_FIT_TO_WIDTH_PAGES = 1;
const UNSET_FIT_TO_HEIGHT_PAGES = 0;

/**
 * The two page budgets for one sheet: the caller's where it has a user choice, the sheet's own Excel page
 * setup where it does not. `fitToPage === false` disables both axes; on each axis a page count of `0` is
 * Excel's "as many pages as it takes" and disables that axis alone.
 *
 * Only a sheet that POSITIVELY asks to fit gets a say: ExcelJS materialises `fitToPage: false` with
 * `fitToWidth/Height: 1` on a sheet that declares nothing, so a `false` is indistinguishable from silence.
 */
const resolveFitPages = (pageSetup: SheetPageSetup | undefined, requested: FitRequest): Pick<PrintJob, "widthPages" | "heightPages"> => {
	if (requested.fitToPage === false) return { widthPages: null, heightPages: null };

	const sheetFit = pageSetup?.fitToPage === true ? pageSetup : undefined;
	const widthPages = requested.fitToWidth ?? sheetFit?.fitToWidth ?? UNSET_FIT_TO_WIDTH_PAGES;
	const heightPages = requested.fitToHeight ?? sheetFit?.fitToHeight ?? UNSET_FIT_TO_HEIGHT_PAGES;
	return { widthPages: widthPages === 0 ? null : widthPages, heightPages: heightPages === 0 ? null : heightPages };
};

const columnWidthsPtOf = (sheet: PrintoutSheet, maxCol: number): number[] => {
	const columnlen = sheet.config?.columnlen ?? {};
	return Array.from({ length: maxCol + 1 }, (_unused, c) => {
		const px = columnlen[String(c)];
		// An unsized column still takes Excel's default width on the page, so it has to be counted. A width
		// of ZERO is not unsized but HIDDEN — hence the type test rather than a truthiness one.
		return (typeof px === "number" ? px : EXCEL_DEFAULT_COLUMN_WIDTH_PX) * PX_TO_PT;
	});
};

const rowHeightsPtOf = (sheet: PrintoutSheet, rowCount: number): number[] => {
	const rowlen = sheet.config?.rowlen ?? {};
	return Array.from({ length: rowCount }, (_unused, r) => {
		const px = rowlen[String(r)];
		return typeof px === "number" && px > 0 ? px * PX_TO_PT : EXCEL_DEFAULT_ROW_HEIGHT_PT;
	});
};

/**
 * The height each row will really be DRAWN at, which is not always the height the sheet asked for:
 * `pinRowHeights` sets `minCellHeight`, and autoTable treats that only as a FLOOR, so an unsized 15pt row
 * carrying a 16pt title is drawn taller however firmly it is pinned. The scan has to budget the drawn
 * height or it picks a scale that only just fails. One line, not the wrapped count — `clipToLines` caps a
 * cell at the lines its height has room for.
 */
const effectiveRowHeightsPt = (doc: jsPDF, sheetHeightsPt: number[], rowFontSizesPt: number[]): number[] => {
	const lineHeightFactor = doc.getLineHeightFactor();
	const scaleFactor = doc.internal.scaleFactor;
	const verticalPaddingPt = CELL_PADDING_PT * 2;
	return sheetHeightsPt.map((heightPt, r) => Math.max(heightPt, (rowFontSizesPt[r] / scaleFactor) * lineHeightFactor + verticalPaddingPt));
};

/** A sheet's drawable columns: the ones Excel actually prints, and its rows renumbered onto them. */
type VisibleColumns = { rows: PositionedCell[][]; columnWidthsPt: number[] };

/**
 * Take out the columns the sheet HIDES — a stored width of zero — and renumber what is left. Excel prints
 * nothing at all for one, so leaving it in shifts every later column across the page; it cannot be drawn
 * zero-wide either, since autoTable subtracts cell padding and the negative becomes a NaN rectangle jsPDF
 * refuses. A merge starting on a hidden column goes with it; no workbook here merges from a hidden origin.
 */
const withoutHiddenColumns = (rows: PositionedCell[][], columnWidthsPt: number[]): VisibleColumns => {
	const visibleCols = columnWidthsPt.flatMap((widthPt, c) => (widthPt > 0 ? [c] : []));
	if (visibleCols.length === columnWidthsPt.length) return { rows, columnWidthsPt };

	const visibleIndexByCol = new Map(visibleCols.map((c, index) => [c, index]));
	const visibleSpan = (startCol: number, colSpan: number): number => {
		let covered = 0;
		for (let c = startCol; c < startCol + colSpan; c++) if (visibleIndexByCol.has(c)) covered++;
		return covered;
	};

	return {
		rows: rows.map((row) =>
			row.flatMap((entry) => {
				const startCol = visibleIndexByCol.get(entry.startCol);
				if (startCol === undefined) return [];
				const colSpan = entry.cell.colSpan === undefined ? undefined : visibleSpan(entry.startCol, entry.cell.colSpan);
				return [{ startCol, cell: colSpan === entry.cell.colSpan ? entry.cell : { ...entry.cell, colSpan } }];
			}),
		),
		columnWidthsPt: visibleCols.map((c) => columnWidthsPt[c]),
	};
};

/** A sheet's rows cut into the three parts Excel's "rows to repeat at top" creates: `head` is the repeated
 *  block, `leading` is whatever sits ABOVE it (the 530's state-form banner) and prints once on page 1, and
 *  `body` is everything after the block and flows. */
type SheetSections = {
	leading: PositionedCell[][];
	head: PositionedCell[][];
	body: PositionedCell[][];
	leadingHeightsPt: number[];
	headHeightsPt: number[];
	bodyHeightsPt: number[];
};

const splitByPrintTitles = (rows: PositionedCell[][], rowHeightsPt: number[], titles: PrintTitleRows | undefined): SheetSections => {
	const withoutTitles: SheetSections = {
		leading: [],
		head: [],
		body: rows,
		leadingHeightsPt: [],
		headHeightsPt: [],
		bodyHeightsPt: rowHeightsPt,
	};
	if (!titles) return withoutTitles;

	const afterTitles = titles.endRow + 1;
	const head = rows.slice(titles.startRow, afterTitles);
	// A range past the sheet's last row repeats nothing; an empty head makes autoTable reserve a
	// zero-height band and reorder the rows above it.
	if (head.length === 0) return withoutTitles;

	return {
		leading: rows.slice(0, titles.startRow),
		head,
		body: rows.slice(afterTitles),
		leadingHeightsPt: rowHeightsPt.slice(0, titles.startRow),
		headHeightsPt: rowHeightsPt.slice(titles.startRow, afterTitles),
		bodyHeightsPt: rowHeightsPt.slice(afterTitles),
	};
};

/**
 * The tallest a cell may ask to be without stretching the rows it covers.
 *
 * autoTable divides a `rowSpan` cell's height into a per-row average and only ever RAISES covered rows to
 * it, so handing it the run's SUM inflates every row shorter than the average. The shortest row of the run
 * is the largest average that raises nothing; it is multiplied back up because autoTable divides.
 */
const spanSafeHeightPt = (rowHeightsPt: number[], startRow: number, rowSpan: number): number => {
	const endRow = Math.min(startRow + rowSpan, rowHeightsPt.length);
	let shortestPt = Number.POSITIVE_INFINITY;
	for (let r = startRow; r < endRow; r++) shortestPt = Math.min(shortestPt, rowHeightsPt[r]);
	return Number.isFinite(shortestPt) ? shortestPt * rowSpan : 0;
};

/**
 * Wraps text the way autoTable's own `linebreak` does, then throws away the lines the row has no room for.
 * Must be an overflow handler, not `didParseCell`, which fires before the final column width is known and
 * still sees raw text. The `+ 1 / scaleFactor` mirrors autoTable's own rounding slack, so the wrap point
 * is unchanged.
 */
const clipToLines = (doc: jsPDF, maxLines: number, fontSizePt: number): OverflowType => {
	return (text: string | string[], widthPt: number): string[] => {
		const source = Array.isArray(text) ? text.join("\n") : text;
		const lines: string[] = doc.splitTextToSize(source, widthPt + 1 / doc.internal.scaleFactor, { fontSize: fontSizePt });
		return lines.slice(0, maxLines);
	};
};

/** Row heights per autoTable section, since a table's head and body index their rows independently. */
type SectionHeightsPt = { head: number[]; body: number[] };

/**
 * Give every row exactly the height its workbook gave it, and clip whatever doesn't fit — Excel fixes the
 * row height and cuts text off at its bottom edge, where autoTable derives height from content. Pinning
 * also decouples height from column width, so shrinking the scale can only ever reduce the page count in
 * each axis independently.
 */
const pinRowHeights = (doc: jsPDF, heightsPt: SectionHeightsPt, scale: number): CellHook => {
	const lineHeightFactor = doc.getLineHeightFactor();
	const scaleFactor = doc.internal.scaleFactor;
	return (data) => {
		const sectionHeightsPt = data.section === "head" ? heightsPt.head : heightsPt.body;
		const heightPt = spanSafeHeightPt(sectionHeightsPt, data.row.index, data.cell.rowSpan) * scale;
		data.cell.styles.minCellHeight = heightPt;

		// The ceiling has to cap the TEXT too: `getContentHeight` takes the larger of the wrapped text and
		// `minCellHeight`, so wrapping into the run's full height would re-inflate the covered rows.
		const fontSizePt = data.cell.styles.fontSize;
		const lineHeightPt = (fontSizePt / scaleFactor) * lineHeightFactor;
		const maxLines = Math.max(1, Math.floor((heightPt - data.cell.padding("vertical")) / lineHeightPt));
		data.cell.styles.overflow = clipToLines(doc, maxLines, fontSizePt);
	};
};

/**
 * autoTable's own line-height constant (`autoTableText`'s `PHYSICAL_LINE_HEIGHT`) and the top-align
 * offset it derives from it. Reproduced because the module exports neither; everything else below is
 * read off the cell autoTable just drew.
 */
const AUTOTABLE_PHYSICAL_LINE_HEIGHT = 1.15;
const AUTOTABLE_TOP_ALIGN_FACTOR = 2 - AUTOTABLE_PHYSICAL_LINE_HEIGHT;

/**
 * Where a rule sits relative to the baseline, and how thick it is, as fractions of the cell's OWN font
 * size. That size has already been through the fit-to pass, so the rules shrink with the text for free
 * and this introduces no new scaled value.
 */
const UNDERLINE_DROP_RATIO = 0.1;
const STRIKE_RISE_RATIO = 0.3;
const RULE_THICKNESS_RATIO = 0.06;

/** `Color` is an RGB triple, a grey level or a CSS string, and `setDrawColor` takes each differently. */
const applyDrawColor = (doc: jsPDF, color: Color): void => {
	if (Array.isArray(color)) doc.setDrawColor(color[0], color[1], color[2]);
	// A bare number is a GREY level — jsPDF's own single-channel form, which its types only expose as the
	// three-channel one, so it is spelled out here rather than cast.
	else if (typeof color === "number") doc.setDrawColor(color, color, color);
	else if (typeof color === "string") doc.setDrawColor(color);
};

/** The decoration `buildSheetBody` attached, narrowed off the cell definition autoTable stored verbatim. */
const textDecorationOf = (raw: unknown): TextDecoration | undefined => {
	if (typeof raw !== "object" || raw === null) return undefined;
	if (!("textDecoration" in raw)) return undefined;
	const decoration = raw.textDecoration;
	if (typeof decoration !== "object" || decoration === null) return undefined;
	if (!("underline" in decoration) || !("strikeThrough" in decoration)) return undefined;
	return { underline: decoration.underline === true, strikeThrough: decoration.strikeThrough === true };
};

/**
 * Draws a cell's underline and strikethrough, which autoTable cannot: it selects a font FACE, and
 * helvetica has no underlined one.
 *
 * Runs as `didDrawCell`, immediately after `autoTableText`, with the document's font state still set to
 * this cell's styles — `printRow` applies them and nothing intervenes — so the size is read back rather
 * than recomputed. The baselines are autoTable's own arithmetic: `Cell.getTextPos()`, then the top-align
 * offset, then a valign shift by the LINE COUNT that `autoTableText` only bothers to count when the
 * alignment needs it, then one line height per line. `cell.text` is the already-wrapped, already-clipped
 * array, so only the lines that actually reached the paper are ruled.
 *
 * Drawn as a STROKED LINE rather than a filled rectangle so that every `re` in the document still means
 * "a cell", which the geometry readers in the test suites rely on to recover the column grid and the
 * drawn scale. The draw colour and line width are left set: autoTable re-applies both before it draws
 * anything else (`printRow` → `applyStyles`, which sets `lineColor` and `lineWidth` per cell), and the
 * page chrome draws text only, so nothing downstream inherits them.
 */
const drawTextDecorations =
	(doc: jsPDF): CellHook =>
	(data) => {
		const { cell } = data;
		const decoration = textDecorationOf(cell.raw);
		if (!decoration) return;

		// The cell's own resolved size, which is what `doc.applyStyles` just set the document's font to —
		// read from the cell rather than the document so nothing depends on jsPDF's untyped internals.
		// Divided by the scale factor exactly as `autoTableText` does (1 for this document's `pt` unit).
		const fontSizePt = cell.styles.fontSize / doc.internal.scaleFactor;
		const lineHeightPt = fontSizePt * doc.getLineHeightFactor();
		const { halign, valign, textColor } = cell.styles;

		// Mirrors `autoTableText` exactly: with the default top/left alignment it never splits the text, so
		// the count stays 1 however many lines were drawn. Guessing `cell.text.length` here instead would
		// put a multi-line top-aligned cell's rules a whole line out.
		const countsLines = valign === "middle" || valign === "bottom" || halign === "center" || halign === "right";
		const lineCount = countsLines ? cell.text.length || 1 : 1;

		const textPos = cell.getTextPos();
		let baselinePt = textPos.y + fontSizePt * AUTOTABLE_TOP_ALIGN_FACTOR;
		if (valign === "middle") baselinePt -= (lineCount / 2) * lineHeightPt;
		else if (valign === "bottom") baselinePt -= lineCount * lineHeightPt;

		applyDrawColor(doc, textColor);
		doc.setLineWidth(fontSizePt * RULE_THICKNESS_RATIO);
		for (const line of cell.text) {
			const widthPt = doc.getTextWidth(line);
			// An empty line gets no rule.
			if (widthPt > 0) {
				const startXPt = textPos.x - (halign === "right" ? widthPt : halign === "center" ? widthPt / 2 : 0);
				const endXPt = startXPt + widthPt;
				if (decoration.underline) {
					const yPt = baselinePt + fontSizePt * UNDERLINE_DROP_RATIO;
					doc.line(startXPt, yPt, endXPt, yPt);
				}
				if (decoration.strikeThrough) {
					const yPt = baselinePt - fontSizePt * STRIKE_RISE_RATIO;
					doc.line(startXPt, yPt, endXPt, yPt);
				}
			}
			baselinePt += lineHeightPt;
		}
	};

type Band = { start: number; end: number };

/** The cells of `rows` that belong on one column band. A merge reaching past the band's last column cannot
 *  continue onto the next physical page, so it is clipped here rather than dragging the scale down. */
const cellsForBand = (rows: PositionedCell[][], band: Band): DecoratedCellDef[][] =>
	rows.map((row) =>
		row
			.filter((entry) => entry.startCol >= band.start && entry.startCol <= band.end)
			.map((entry) => {
				const maxColSpan = band.end - entry.startCol + 1;
				if (!entry.cell.colSpan || entry.cell.colSpan <= maxColSpan) return entry.cell;
				return { ...entry.cell, colSpan: maxColSpan };
			}),
	);

type BandArgs = {
	sections: SheetSections;
	columnWidthsPt: number[];
	scale: number;
	margins: PageMarginsPt;
	/** Extra indent on both sides that centres the band within the printable width; 0 when off. */
	centeringOffsetPt: number;
	band: Band;
};

const drawBand = (doc: jsPDF, { sections, columnWidthsPt, scale, margins, centeringOffsetPt, band }: BandArgs): void => {
	const columnStyles: Record<number, Partial<Styles>> = {};
	for (let c = band.start; c <= band.end; c++) columnStyles[c - band.start] = { cellWidth: columnWidthsPt[c] * scale };

	const common = {
		// Without `columns`, autoTable takes its column count from `head[0] || body[0]` — and an empty array
		// is truthy, so a band whose row 0 has no cells in range renders nothing. Each entry must stay an
		// object with no `header`: a plain number becomes an auto-generated head row of column indices.
		columns: Array.from({ length: band.end - band.start + 1 }, (_unused, i) => ({ dataKey: i })),
		theme: "grid" as const,
		// Excel breaks between whole rows only; autoTable's default splits a row across the boundary.
		rowPageBreak: "avoid" as const,
		styles: {
			fontSize: BODY_FONT_SIZE_PT * scale,
			cellPadding: CELL_PADDING_PT * scale,
			lineWidth: GRID_LINE_WIDTH_PT * scale,
			lineColor: GRID_LINE_COLOR,
			overflow: "linebreak" as const,
			// Excel's own default vertical alignment; a cell that declared one overrides this per cell.
			valign: "bottom" as const,
		},
		headStyles: { fillColor: PLAIN_BODY_FILL_COLOR, textColor: PLAIN_BODY_TEXT_COLOR, fontStyle: "normal" as const },
		columnStyles,
		// Physical, and therefore never scaled — Excel shrinks the content inside the margins, not the
		// margins. `startY` matches `margin.top` so page 1 holds as many rows as a continuation page.
		margin: {
			left: margins.left + centeringOffsetPt,
			right: margins.right + centeringOffsetPt,
			top: margins.top,
			bottom: margins.bottom,
		},
	};

	// Rows above the repeated block print once, before its first copy. autoTable can only draw a head at
	// the very top of a table, so they need a table of their own.
	let flowStartY = margins.top;
	if (sections.leading.length > 0) {
		autoTable(doc, {
			...common,
			body: cellsForBand(sections.leading, band),
			startY: margins.top,
			didParseCell: pinRowHeights(doc, { head: [], body: sections.leadingHeightsPt }, scale),
			didDrawCell: drawTextDecorations(doc),
			// The last call lands on the table's own end, so this is where the next table must start.
			didDrawPage: (data) => {
				if (data.cursor) flowStartY = data.cursor.y;
			},
		});
	}

	autoTable(doc, {
		...common,
		head: cellsForBand(sections.head, band),
		body: cellsForBand(sections.body, band),
		// Excel's whole point in repeating them: every printed page, not just the first.
		showHead: "everyPage",
		startY: flowStartY,
		didParseCell: pinRowHeights(doc, { head: sections.headHeightsPt, body: sections.bodyHeightsPt }, scale),
		// On BOTH tables: the leading band above a repeated print-title block is a table of its own, and a
		// decoration applied to a cell there would otherwise silently not draw.
		didDrawCell: drawTextDecorations(doc),
	});
};

const runsWidthPt = (doc: jsPDF, runs: HeaderFooterRun[]): number =>
	runs.reduce((total, run) => {
		doc.setFont(HEADER_FOOTER_FONT_FACE, run.bold ? "bold" : "normal");
		return total + doc.getTextWidth(run.text);
	}, 0);

const drawRuns = (doc: jsPDF, runs: HeaderFooterRun[], startXPt: number, baselineYPt: number): void => {
	let xPt = startXPt;
	for (const run of runs) {
		doc.setFont(HEADER_FOOTER_FONT_FACE, run.bold ? "bold" : "normal");
		doc.text(run.text, xPt, baselineYPt);
		xPt += doc.getTextWidth(run.text);
	}
};

/** Left at the left margin, right at the right margin, centre between the two — Excel's three boxes. */
const drawSections = (doc: jsPDF, sections: HeaderFooterSections, margins: PageMarginsPt, baselineYPt: number): void => {
	doc.setFontSize(HEADER_FOOTER_FONT_SIZE_PT);
	const leftEdgePt = margins.left;
	const rightEdgePt = doc.internal.pageSize.getWidth() - margins.right;

	drawRuns(doc, sections.left, leftEdgePt, baselineYPt);
	drawRuns(doc, sections.center, (leftEdgePt + rightEdgePt - runsWidthPt(doc, sections.center)) / 2, baselineYPt);
	drawRuns(doc, sections.right, rightEdgePt - runsWidthPt(doc, sections.right), baselineYPt);
};

/**
 * Draw one page's header and footer from the sheet's own Excel strings. Both live in the margin bands, so
 * they cost the body no rows, and neither is scaled: Excel's fit-to zoom applies to the sheet, not to page
 * chrome. A sheet that declares neither gets nothing drawn.
 */
const drawPageChrome = (doc: jsPDF, sheet: PrintoutSheet, fields: HeaderFooterFields): void => {
	const header = parseExcelHeaderFooter(sheet.pageSetup?.oddHeader, fields);
	const footer = parseExcelHeaderFooter(sheet.pageSetup?.oddFooter, fields);
	if (!header && !footer) return;

	const margins = resolveMarginsPt(sheet.pageSetup);
	// The header sits `margins.header` below the paper edge; one font size lower is its baseline. Clamped
	// to the top margin so a document with an unusually large header margin can't overprint the table.
	if (header) drawSections(doc, header, margins, Math.min(margins.header + HEADER_FOOTER_FONT_SIZE_PT, margins.top));
	if (footer) drawSections(doc, footer, margins, doc.internal.pageSize.getHeight() - margins.footer);
};

type PrintJob = {
	/** `null` when fit-to-width is off; otherwise the number of page-widths the sheet must fit within. */
	widthPages: number | null;
	/** `null` when fit-to-height is off; otherwise the number of page-heights it must fit within. */
	heightPages: number | null;
	startPage: () => void;
};

/** Draws one worksheet, and reports whether it put anything on paper. A sheet that printed nothing must
 *  claim no page range, or the next sheet's pages are attributed to it and get its header and footer. */
const renderSheet = (doc: jsPDF, sheet: PrintoutSheet, { widthPages, heightPages, startPage }: PrintJob): boolean => {
	const body = buildSheetBody(sheet);
	const { explicitFonts, rowFontSizesPt } = body;
	const margins = resolveMarginsPt(sheet.pageSetup);
	const availableWidthPt = doc.internal.pageSize.getWidth() - margins.left - margins.right;
	const availableHeightPt = doc.internal.pageSize.getHeight() - margins.top - margins.bottom;

	const { rows, columnWidthsPt } = withoutHiddenColumns(body.rows, columnWidthsPtOf(sheet, body.maxCol));
	// A sheet whose every column is hidden prints nothing in Excel, so it must add no page here either:
	// `packIntoPageRuns` returns `{ start: 0, end: -1 }` for an empty width list, and that band starts a
	// page and draws a zero-column table on it — a blank sheet of paper in the middle of the job.
	if (columnWidthsPt.length === 0) return false;
	// The drawn height, not the sheet's — the scan and `pinRowHeights` share one array so the budget and
	// the paper cannot disagree.
	const rowHeightsPt = effectiveRowHeightsPt(doc, rowHeightsPtOf(sheet, rows.length), rowFontSizesPt);
	const sections = splitByPrintTitles(rows, rowHeightsPt, sheet.pageSetup?.printTitleRows);
	const scale =
		resolveScalePercent({
			columnWidthsPt,
			flowRowHeightsPt: [...sections.leadingHeightsPt, ...sections.bodyHeightsPt],
			printTitleHeightPt: sumPt(sections.headHeightsPt),
			availableWidthPt,
			availableHeightPt,
			widthPages,
			heightPages,
		}) / PERCENT;

	// From the stored original, once per sheet: the style objects are shared by every band, so scaling
	// them from their current value would compound.
	for (const font of explicitFonts) font.styles.fontSize = font.originalFontSizePt * scale;

	// Unconditional even with fit-to-width off (`scale` is 1 then): Excel's `fitToWidth = 0` means tiled
	// at natural size across several physical pages, not squeezed onto one with the overflow lost.
	for (const band of packIntoPageRuns(columnWidthsPt, scale, availableWidthPt)) {
		startPage();
		const bandWidthPt = sumPt(columnWidthsPt.slice(band.start, band.end + 1)) * scale;
		// Excel's `horizontalCentered` centres the printed range between the left and right margins.
		const centeringOffsetPt = sheet.pageSetup?.horizontalCentered === true ? Math.max(0, (availableWidthPt - bandWidthPt) / 2) : 0;
		drawBand(doc, { sections, columnWidthsPt, scale, margins, centeringOffsetPt, band });
	}
	return true;
};

/** Which physical pages one sheet ended up occupying, so its own header and footer can be drawn on them. */
type SheetPageRange = { sheet: PrintoutSheet; firstPage: number; lastPage: number };

/** Stamp every page's header and footer AFTER the whole document exists: `&N` needs the finished document,
 *  and `&P` is the position in the print job, which Excel numbers straight through a multi-worksheet job. */
const drawAllPageChrome = (doc: jsPDF, ranges: SheetPageRange[]): void => {
	const printedAt = DateTime.now();
	const date = printedAt.toLocaleString(DateTime.DATE_SHORT);
	const time = printedAt.toLocaleString(DateTime.TIME_SIMPLE);
	const totalPages = doc.getNumberOfPages();

	for (const range of ranges) {
		for (let pageNumber = range.firstPage; pageNumber <= range.lastPage; pageNumber++) {
			doc.setPage(pageNumber);
			drawPageChrome(doc, range.sheet, { date, time, pageNumber, totalPages });
		}
	}
};

/**
 * Renders Daily, 530, Monthly, Disciplinary, Facility and Week-staff printouts to PDF; the Weekly staffing
 * grid has its own `@react-pdf/renderer` pipeline (`StaffingPDF`) and does not come through here.
 *
 * The PDF is a second rendering of the SAME workbook the xlsx download contains, so paper, margins,
 * repeated header rows, centering and page chrome come from the sheet's own Excel page setup, and "fit to
 * W pages by H" resolves as Excel does: one whole-percent zoom, 10 to 100. Every argument after `sheets`
 * is a SETTING A USER PICKED, or `null` where the caller has none — see `resolveOrientation` and
 * `resolveFitPages` for what a `null` defers to, and why a sheet is not always believed.
 */
export const generatePrintoutPdf = (
	sheets: PrintoutSheet[],
	orientation: PrintoutOrientation | null,
	fitToPage: boolean | null,
	fitToWidth: number | null,
	fitToHeight: number | null,
): Blob => {
	const paperPt = resolvePaperPt(sheets);
	const doc = new jsPDF({
		orientation: resolveOrientation(sheets, orientation) === PrintoutOrientation.Landscape ? "landscape" : "portrait",
		unit: "pt",
		// Portrait dimensions; jsPDF swaps them itself for a landscape document.
		format: [paperPt.widthPt, paperPt.heightPt],
	});

	const requestedFit: FitRequest = { fitToPage, fitToWidth, fitToHeight };

	let isFirstPage = true;
	const startPage = (): void => {
		if (!isFirstPage) doc.addPage();
		isFirstPage = false;
	};

	// All vertical pages of band 1, then band 2 — Excel's default `downThenOver` page order.
	const ranges: SheetPageRange[] = [];
	let firstPage = 1;
	for (const sheet of sheets) {
		// Per sheet, not per document: the fit-to setup lives on the worksheet, exactly as margins do.
		// A sheet that printed nothing claims no range: `startPage` never ran, so the pages counted here
		// belong to the sheets around it, and recording them would stamp this sheet's header and footer
		// over the next sheet's first page and leave that sheet with none of its own.
		if (!renderSheet(doc, sheet, { ...resolveFitPages(sheet.pageSetup, requestedFit), startPage })) continue;
		const lastPage = doc.getNumberOfPages();
		ranges.push({ sheet, firstPage, lastPage });
		firstPage = lastPage + 1;
	}
	drawAllPageChrome(doc, ranges);

	return doc.output("blob");
};
